import { requireAuth } from '@/features/auth/guards/require-auth'
import { WorkOrderDetailPage } from '@/features/work-orders/pages/work-order-detail-page'
import {
  woMaterialIssuesQuery,
  woProductionReportsQuery,
} from '@/features/work-orders/queries/phase12-queries'
import {
  cutPlanLinesQuery,
  issueLinesQuery,
  outputLinesQuery,
  projectDeficitsQuery,
  taskLinesQuery,
  workOrderQuery,
} from '@/features/work-orders/queries/work-order-detail-query'
import { messages } from '@/lib/i18n/messages'
import { createFileRoute } from '@tanstack/react-router'
import * as z from 'zod'

const paramsSchema = z.object({
  workOrderId: z.coerce.number().int().positive(),
})

/**
 * Global Work Order detail — opened from the «Документи > Наряди» sidebar
 * group. Same content as the project-scoped variant
 * (`/projects/$pid/_layout/work-orders/$workOrderId`), but rendered inside the
 * plain Shell so the «Поточний проєкт» sidebar section doesn't activate
 * and the active sidebar entry stays «Наряди» in Документи.
 *
 * Matches the editor-route pattern used by MIN / PR / SR / GR.
 */
export const Route = createFileRoute('/_app/work-orders/$workOrderId')({
  beforeLoad: requireAuth,
  parseParams: paramsSchema.parse,
  stringifyParams: ({ workOrderId }) => ({ workOrderId: String(workOrderId) }),
  loader: async ({ params, context }) => {
    const qc = context.queryClient
    const wo = await qc.ensureQueryData(workOrderQuery(params.workOrderId))
    const fetches: Promise<unknown>[] = [
      qc.ensureQueryData(taskLinesQuery(params.workOrderId)),
      qc.ensureQueryData(issueLinesQuery(params.workOrderId)),
      qc.ensureQueryData(outputLinesQuery(params.workOrderId)),
      qc.ensureQueryData(projectDeficitsQuery(wo.project_id)),
      qc.ensureQueryData(woMaterialIssuesQuery(wo.project_id, params.workOrderId)),
      qc.ensureQueryData(woProductionReportsQuery(wo.project_id, params.workOrderId)),
    ]
    // Cut-plan lines — лише cutter (welder не має розкрою).
    if (wo.work_order_type === 'cutter') {
      fetches.push(qc.ensureQueryData(cutPlanLinesQuery(params.workOrderId)))
    }
    await Promise.all(fetches)
    return { number: wo.number }
  },
  // Crumb: «<список> › <номер документа>» — the number from the loader, not the id.
  staticData: {
    crumbParent: { label: messages.crumbs.workOrders, to: '/work-orders/' },
    crumb: (m: { loaderData?: { number: string } }) => m.loaderData?.number ?? '…',
  },
  component: () => {
    const { workOrderId } = Route.useParams()
    return <WorkOrderDetailPage workOrderId={workOrderId} />
  },
})
